import { z } from 'zod';

/**
 * Treatment Report Base Schema (R2 - less strict)
 */
export const treatmentReportSchema = z.object({
  treatment_yearmonth: z.preprocess(
    (v) => (v == null ? undefined : v),
    z.number({ required_error: '施術年月は必須です。', invalid_type_error: '施術年月は必須です。' })
  ),
  treatment_report_date: z
    .string({ required_error: '作成年月日は必須です。' })
    .min(1, '作成年月日は必須です。'),
  // 施術の内容: positions are auto-displayed from the consent; this field is the
  // optional free comment for content beyond positions (BD HIT-4357). Not required.
  contents_frequency: z
    .string()
    .max(300, '施術の内容は300文字以内で入力してください。')
    .optional()
    .nullable(),
  massager_id: z
    .number({ required_error: '施術師は必須です。' })
    .min(1, '施術師は必須です。'),
  clinic_location_id: z.preprocess(
    v => (v == null || v === '' ? undefined : v),
    z
      .number({
        required_error: '拠点住所を選択してください。',
        invalid_type_error: '拠点住所を選択してください。',
      })
      .min(1, '拠点住所を選択してください。')
  ),
  is_treatment_report_default: z.boolean().default(false),
  second_flag: z.boolean().optional().nullable(),
  hospital_id: z.number().optional().nullable(),
  doctor_id: z.number().optional().nullable(),
  treatment_monthly_avg: z.preprocess(
    (val) => (val === null || val === '' ? undefined : val),
    z.number({ required_error: '施術の頻度は必須です。', invalid_type_error: '施術の頻度は必須です。' })
      // 0 is not a valid frequency: same error as a blank value (UAT No202).
      .min(1, '施術の頻度は必須です。')
      .max(999, '施術の頻度は3桁以内で入力してください。')
  ),
  status_course: z
    .string({ required_error: '患者の状態・経過は必須です。' })
    .min(1, '患者の状態・経過は必須です。')
    .max(300, '患者の状態・経過は300文字以内で入力してください。'),
  remarks: z.string().max(300, '特記すべき事項は300文字以内で入力してください。').optional().nullable(),
  location_name: z.string().max(255).optional().nullable(),
  postal_code1: z.preprocess(
    v => (v == null || v === '' ? undefined : v),
    z.string().length(3, '郵便番号1は3桁にしてください。').optional()
  ),
  postal_code2: z.preprocess(
    v => (v == null || v === '' ? undefined : v),
    z.string().length(4, '郵便番号2は4桁にしてください。').optional()
  ),
  address: z.string().max(255).optional().nullable(),
  telephone: z.string().regex(/^$|^[0-9\-]{7,13}$/, '電話番号の書式が正しくありません。').optional().nullable(),
  fax: z.string().regex(/^$|^[0-9\-]{7,13}$/, 'FAX番号の書式が正しくありません。').optional().nullable(),
  email: z.string().email('メールアドレスを正しいメールアドレスにしてください。').or(z.literal('')).optional().nullable(),
  non_print_cols: z.array(z.string()).default([]),
});

/**
 * A2 Treatment Report Schema - currently identical to base since base was tightened.
 */
export const a2TreatmentReportSchema = treatmentReportSchema;

export type TreatmentReportFormData = z.infer<typeof a2TreatmentReportSchema>;
